import { Pill } from '../../components/ui/Pill';
import type { ShippingPromotion } from '../../lib/api/operatorConsole';
import {
  formatCap,
  formatCovers,
  formatLocalTime,
  formatValue,
  statusBadge,
} from './shippingPromotions.helpers';

const COLUMNS = ['Promotion', 'Offer', 'Covers', 'Max discount', 'Window', 'Status', ''];

const headCell =
  'px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';
const cell = 'px-4 py-3 align-top text-[13px] text-[var(--co-text)]';
const action =
  'rounded-lg border border-[var(--co-border)] px-3 py-1 text-[12px] font-medium text-[var(--co-text)] ' +
  'transition-colors hover:bg-[var(--co-bg)]';

interface Props {
  promotions: ShippingPromotion[];
  currency: string;
  onEdit: (promotion: ShippingPromotion) => void;
  onDisable: (promotion: ShippingPromotion) => void;
}

export function PromotionsTable({ promotions, currency, onEdit, onDisable }: Props) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
      <table aria-label="Shipping promotions" className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--co-border)]">
            {COLUMNS.map((column, index) => (
              <th key={column || `actions-${index}`} scope="col" className={headCell}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {promotions.map((promotion) => {
            const status = statusBadge(promotion.status);
            return (
              <tr key={promotion.id} className="border-b border-[var(--co-border)] last:border-b-0">
                <td className={`${cell} font-medium`}>{promotion.label}</td>
                <td className={cell}>{formatValue(promotion, currency)}</td>
                <td className={cell}>{formatCovers(promotion.covers)}</td>
                <td className={`${cell} font-mono`}>{formatCap(promotion.max_discount, currency)}</td>
                <td className={cell}>
                  <div>{formatLocalTime(promotion.starts_at)}</div>
                  <div className="text-[var(--co-muted)]">to {formatLocalTime(promotion.ends_at)}</div>
                </td>
                <td className={cell}>
                  <Pill tone={status.tone}>{status.label}</Pill>
                </td>
                <td className={`${cell} whitespace-nowrap text-right`}>
                  {/* The backend decides: only a scheduled or active promotion can still change. */}
                  {promotion.editable && (
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(promotion)}
                        aria-label={`Edit ${promotion.label}`}
                        className={action}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDisable(promotion)}
                        aria-label={`Disable ${promotion.label}`}
                        className={`${action} hover:text-[var(--co-error)]`}
                      >
                        Disable
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
